import React, { useEffect, useRef, useState } from "react";
import { ReactComponent as InternetIcon } from "@assets/Icons/IconGlobe.svg";
import { ReactComponent as PortIcon } from "@assets/Icons/IconEthernet.svg";
import { ReactComponent as CloudIcon } from "@assets/Icons/IconCloudPlug.svg";
import { ReactComponent as DatacenterIcon } from "@assets/Icons/IconDcInterconnect.svg";
import { ReactComponent as FwaasIcon } from "@assets/Icons/IconShieldHalved.svg";
import { ReactComponent as SdwanIcon } from "@assets/Icons/IconSitemap.svg";
import { KindHealth, ServiceHealth, ServiceKindKey, ServiceScope, worstHealth } from "./overviewModel";
import {
  ChipIssues,
  PRINCIPAL_CHIPS,
  ScopeChipItem,
  ScopeChipKey,
  readPinnedScopeChips,
  splitScopeChips,
  sumIssues,
  togglePinnedScopeChip,
  writePinnedScopeChips,
} from "./scopeChipsModel";
import { CpeGlyph } from "./topologyParts";

export type { ChipIssues } from "./scopeChipsModel";

/** The SideMenu's icon for each kind, so a chip and its menu entry look alike. */
const KIND_ICON: Record<ServiceKindKey, React.ComponentType<React.SVGProps<SVGSVGElement>>> = {
  ports: PortIcon,
  internet: InternetIcon,
  cloudConnectivity: CloudIcon,
  datacenterInterconnect: DatacenterIcon,
  fwaas: FwaasIcon,
  sdwan: SdwanIcon,
};

export const HEALTH_DOT: Record<ServiceHealth, string> = {
  up: "bg-semantic-fg-success",
  warning: "bg-semantic-fg-warning",
  down: "bg-semantic-fg-danger",
  unknown: "bg-semantic-fg-secondary",
};

const HEALTH_LABEL: Record<ServiceHealth, string> = {
  up: "all up",
  warning: "degraded",
  down: "down",
  unknown: "no data",
};

const ISSUE_BADGE = {
  critical: "bg-semantic-bg-danger-secondary text-semantic-text-danger",
  warning: "bg-semantic-bg-warning-secondary text-semantic-text-warning",
};

const NO_ISSUES: ChipIssues = { count: 0, isCritical: false };

function kindIssues(kind: KindHealth): ChipIssues {
  return { count: kind.down + kind.warning, isCritical: kind.down > 0 };
}

/** "3 down", "1 degraded", "all up" — the state a chip's tooltip reads out. */
function describeKind(kind: KindHealth): string {
  if (kind.down > 0) return `${kind.down} down`;
  if (kind.warning > 0) return `${kind.warning} degraded`;
  return HEALTH_LABEL[worstHealth(kind)];
}

const CHIP_TONE = {
  selected: "border-semantic-border-brand-primary bg-semantic-bg-brand-secondary text-semantic-text-brand",
  idle: "border-semantic-border-primary bg-semantic-bg-primary text-semantic-text-secondary hover:bg-semantic-hover-primary",
};

/** Only trouble is marked: a green dot on every healthy chip says nothing. */
function IssueBadge({ issues }: { issues: ChipIssues }) {
  if (issues.count === 0) return null;
  return (
    <span
      className={`-mr-1.5 flex h-5 min-w-5 items-center justify-center rounded-full px-1.5 text-[11px] font-bold tabular-nums ${
        ISSUE_BADGE[issues.isCritical ? "critical" : "warning"]
      }`}
    >
      {issues.count}
    </span>
  );
}

function Chip({
  selected,
  onClick,
  icon,
  label,
  count,
  issues,
  title,
  className = "",
}: {
  selected: boolean;
  onClick: () => void;
  icon?: React.ReactNode;
  label: string;
  count: number;
  issues: ChipIssues;
  title: string;
  className?: string;
}) {
  // A phone gets a grid cell (label left, count right); wider screens the pill.
  return (
    <button
      type="button"
      aria-pressed={selected}
      title={title}
      onClick={onClick}
      className={`flex h-9 w-full min-w-0 items-center gap-2 rounded-lg border px-3 text-xs font-semibold transition-colors sm:inline-flex sm:h-8 sm:w-auto sm:shrink-0 sm:rounded-full ${
        CHIP_TONE[selected ? "selected" : "idle"]
      } ${className}`}
    >
      {icon}
      <span className="min-w-0 flex-1 truncate text-left sm:flex-none">{label}</span>
      <span className="tabular-nums text-semantic-text-primary">{count}</span>
      <IssueBadge issues={issues} />
    </button>
  );
}

/** What a chip shows for a kind (or the CPEs): its name, icon and tooltip. */
interface ChipMeta {
  label: string;
  icon: React.ReactNode;
  title: string;
}

function PinGlyph({ filled }: { filled: boolean }) {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4" fill={filled ? "currentColor" : "none"} stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M12 17v5" />
      <path d="M9 10.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24V16a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-.76a2 2 0 0 0-1.11-1.79l-1.78-.9A2 2 0 0 1 15 10.76V7a1 1 0 0 1 1-1 2 2 0 0 0 0-4H8a2 2 0 0 0 0 4 1 1 0 0 1 1 1z" />
    </svg>
  );
}

const PIN_TONE = {
  pinned: "text-semantic-text-brand hover:bg-semantic-hover-primary",
  unpinned: "text-semantic-text-secondary hover:bg-semantic-hover-primary hover:text-semantic-text-brand",
};

/**
 * «All»: every kind the tenant has, the ones on the bar too (CTX-1488). Its
 * badge adds up the trouble of the kinds off the bar, so none of them hides a
 * problem; a kind picked from it shows on the chip itself. Each entry carries
 * its pin, blue while it is on the bar: that is how a viewer picks theirs.
 */
function AllKindsMenu({
  items,
  offBar,
  pinned,
  metaOf,
  scope,
  onScopeChange,
  onTogglePin,
}: {
  items: ScopeChipItem[];
  offBar: ScopeChipItem[];
  pinned: ScopeChipKey[];
  metaOf: (key: ScopeChipKey) => ChipMeta;
  scope: ServiceScope;
  onScopeChange: (scope: ServiceScope) => void;
  onTogglePin: (key: ScopeChipKey) => void;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return undefined;
    const closeOutside = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setIsOpen(false);
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setIsOpen(false);
    };
    document.addEventListener("mousedown", closeOutside);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("mousedown", closeOutside);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [isOpen]);

  const picked = offBar.find((item) => item.key === scope);
  const issues = sumIssues(offBar);
  return (
    <div ref={rootRef} className="relative shrink-0">
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={isOpen}
        title={picked ? metaOf(picked.key).title : `All ${items.length} service types; pin the ones you want on the bar`}
        onClick={() => setIsOpen((open) => !open)}
        className={`inline-flex h-8 items-center gap-2 rounded-full border px-3 text-xs font-semibold transition-colors ${
          CHIP_TONE[picked ? "selected" : "idle"]
        }`}
      >
        {picked ? metaOf(picked.key).icon : null}
        <span>{picked ? metaOf(picked.key).label : "All"}</span>
        <span className="tabular-nums text-semantic-text-primary">{picked ? picked.total : items.length}</span>
        <IssueBadge issues={picked ? picked.issues : issues} />
        <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>
      {isOpen && (
        <div
          role="menu"
          aria-label="All service types"
          className="absolute right-0 top-full z-30 mt-1 w-64 rounded-lg border border-semantic-border-primary bg-semantic-bg-primary p-1 shadow-md"
        >
          {items.map((item) => {
            const meta = metaOf(item.key);
            const isPinned = pinned.includes(item.key);
            return (
              <div key={item.key} className="flex items-center gap-1">
                <button
                  type="button"
                  role="menuitemradio"
                  aria-checked={scope === item.key}
                  title={meta.title}
                  onClick={() => {
                    onScopeChange(item.key);
                    setIsOpen(false);
                  }}
                  className={`flex h-9 min-w-0 flex-1 items-center gap-2 rounded-md px-2 text-left text-xs font-semibold hover:bg-semantic-hover-primary ${
                    scope === item.key ? "text-semantic-text-brand" : "text-semantic-text-primary"
                  }`}
                >
                  <span className="text-semantic-text-secondary">{meta.icon}</span>
                  <span className="min-w-0 flex-1 truncate">{meta.label}</span>
                  <span className="tabular-nums text-semantic-text-secondary">{item.total}</span>
                  <IssueBadge issues={item.issues} />
                </button>
                {/* The menu stays open, so several kinds can be pinned in one go. */}
                <button
                  type="button"
                  aria-pressed={isPinned}
                  aria-label={isPinned ? `Unpin ${meta.label} from the bar` : `Pin ${meta.label} to the bar`}
                  title={isPinned ? "Unpin from the bar" : "Pin to the bar"}
                  onClick={() => onTogglePin(item.key)}
                  className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-md ${PIN_TONE[isPinned ? "pinned" : "unpinned"]}`}
                >
                  <PinGlyph filled={isPinned} />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/** How far the lane fades out at an edge with more chips past it, in px: room for its arrow. */
const LANE_FADE_PX = 40;
/** How much of the lane's width one arrow click moves it. */
const LANE_STEP = 0.7;

type LaneSide = "start" | "end";

const ARROW_PLACE: Record<LaneSide, string> = { start: "left-0", end: "right-0" };
const ARROW_PATH: Record<LaneSide, string> = { start: "M15 6l-6 6 6 6", end: "M9 6l6 6-6 6" };

/** The arrow at one end of the lane, for a mouse with no sideways scroll (Daniel, 2026-10-27). */
function LaneArrow({ side, onClick }: { side: LaneSide; onClick: () => void }) {
  return (
    <button
      type="button"
      aria-label={side === "start" ? "Scroll service types left" : "Scroll service types right"}
      onClick={onClick}
      className={`absolute top-1/2 ${ARROW_PLACE[side]} z-10 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-full border border-semantic-border-primary bg-semantic-bg-primary text-semantic-text-secondary shadow-sm transition-colors hover:bg-semantic-hover-primary hover:text-semantic-text-primary`}
    >
      <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <path d={ARROW_PATH[side]} />
      </svg>
    </button>
  );
}

/**
 * The pinned chips, in a lane that scrolls sideways once they no longer fit
 * (Daniel, 2026-10-27). The scrollbar stays hidden; an edge with chips past it
 * fades out and carries an arrow instead, a mouse wheel scrolls it, and the
 * selected chip is brought into view. "All services" and «All» sit outside it,
 * so they never scroll away and the menu is not clipped.
 */
function ChipLane({
  children,
  contentKey,
  selectedKey,
}: {
  children: React.ReactNode;
  /** Changes whenever the chips do, so the fades are measured again. */
  contentKey: string;
  selectedKey: ServiceScope;
}) {
  const laneRef = useRef<HTMLDivElement>(null);
  const [edges, setEdges] = useState({ start: false, end: false });

  useEffect(() => {
    const lane = laneRef.current;
    if (!lane) return undefined;
    const measure = () =>
      setEdges({
        start: lane.scrollLeft > 1,
        end: lane.scrollLeft + lane.clientWidth < lane.scrollWidth - 1,
      });
    // A vertical wheel scrolls the lane while it can move; past either end the page scrolls.
    const onWheel = (event: WheelEvent) => {
      if (lane.scrollWidth <= lane.clientWidth || Math.abs(event.deltaY) <= Math.abs(event.deltaX)) return;
      const before = lane.scrollLeft;
      lane.scrollLeft += event.deltaY;
      if (lane.scrollLeft !== before) event.preventDefault();
    };
    measure();
    lane.addEventListener("scroll", measure, { passive: true });
    lane.addEventListener("wheel", onWheel, { passive: false });
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(measure);
    observer?.observe(lane);
    return () => {
      lane.removeEventListener("scroll", measure);
      lane.removeEventListener("wheel", onWheel);
      observer?.disconnect();
    };
  }, [contentKey]);

  useEffect(() => {
    const selected = laneRef.current?.querySelector<HTMLElement>('[aria-pressed="true"]');
    selected?.scrollIntoView?.({ block: "nearest", inline: "nearest", behavior: "smooth" });
  }, [selectedKey]);

  const step = (side: LaneSide) => {
    const lane = laneRef.current;
    if (!lane) return;
    const by = lane.clientWidth * LANE_STEP * (side === "start" ? -1 : 1);
    lane.scrollBy?.({ left: by, behavior: "smooth" });
  };
  const mask = `linear-gradient(to right, ${edges.start ? "transparent" : "black"}, black ${LANE_FADE_PX}px, black calc(100% - ${LANE_FADE_PX}px), ${
    edges.end ? "transparent" : "black"
  })`;
  // The arrows sit beside the lane, not in it: the fade would swallow them.
  return (
    <div className="relative flex min-w-0">
      <div
        ref={laneRef}
        className="scrollbar-hide flex min-w-0 items-center gap-2 overflow-x-auto"
        style={{ maskImage: mask, WebkitMaskImage: mask }}
      >
        {children}
      </div>
      {edges.start && <LaneArrow side="start" onClick={() => step("start")} />}
      {edges.end && <LaneArrow side="end" onClick={() => step("end")} />}
    </div>
  );
}

/**
 * One chip per service kind with its count and, when something is wrong, how
 * many need a look; plus "All services". Kinds the tenant does not have are
 * left out: a row of zeros is noise.
 *
 * Wider than a phone the chips keep to one row (CTX-1488): "All services", the
 * kinds the viewer pinned (the first four until they choose; as many as they
 * like, in a lane that scrolls) and «All», which lists every kind and is where
 * pins are set.
 */
export function ServiceScopeChips({
  kinds,
  cpeCount = 0,
  openIssues,
  scope,
  onScopeChange,
}: {
  kinds: KindHealth[];
  /** The scope's CPEs; no chip without any. */
  cpeCount?: number;
  /**
   * Everything open across kinds, ongoing outages included — the list's own
   * count, so "All services" and the list header never disagree.
   */
  openIssues: ChipIssues;
  scope: ServiceScope;
  onScopeChange: (scope: ServiceScope) => void;
}) {
  const present = kinds.filter((kind) => kind.total > 0);
  // Ports are infrastructure, not services: "All services" counts services only.
  // Their issues still count in its badge.
  const serviceCount = present
    .filter((kind) => kind.key !== "ports")
    .reduce((sum, kind) => sum + kind.total, 0);
  const totals = present.reduce(
    (acc, kind) => ({
      total: acc.total + kind.total,
      up: acc.up + kind.up,
      warning: acc.warning + kind.warning,
      down: acc.down + kind.down,
    }),
    { total: 0, up: 0, warning: 0, down: 0 }
  );

  const items: ScopeChipItem[] = [
    ...present.map((kind) => ({ key: kind.key, total: kind.total, issues: kindIssues(kind) })),
    ...(cpeCount > 0 ? [{ key: "cpe" as const, total: cpeCount, issues: NO_ISSUES }] : []),
  ];
  const metaOf = (key: ScopeChipKey): ChipMeta => {
    if (key === "cpe") {
      return {
        label: "CPE",
        icon: <CpeGlyph className="h-4 w-4" />,
        title: `CPE: ${cpeCount} customer premises equipment, with where their Backhaul lands`,
      };
    }
    const kind = present.find((candidate) => candidate.key === key) as KindHealth;
    const Icon = KIND_ICON[key];
    return { label: kind.label, icon: <Icon className="h-4 w-4 shrink-0" aria-hidden />, title: `${kind.label}: ${describeKind(kind)}` };
  };

  // null until the viewer pins or unpins: the defaults hold and follow the tenant.
  const [savedPins, setSavedPins] = useState<ScopeChipKey[] | null>(readPinnedScopeChips);
  const { principal, more } = splitScopeChips(items, savedPins);
  // The pins as this tenant sees them: a kind it lacks takes no place on the bar.
  const pinned = principal.map((item) => item.key);
  const togglePin = (key: ScopeChipKey) => {
    const next = togglePinnedScopeChip(pinned, key);
    setSavedPins(next);
    writePinnedScopeChips(next);
  };
  // Four kinds or fewer all sit on the bar until the viewer chooses: nothing to pick from.
  const isChoosable = items.length > PRINCIPAL_CHIPS;
  const onBar = isChoosable ? principal : items;

  const allChip = (className?: string) => (
    <Chip
      className={className}
      selected={scope === "all"}
      onClick={() => onScopeChange("all")}
      label="All services"
      count={serviceCount}
      issues={openIssues}
      title={`All services: ${describeKind({ key: "ports", label: "", unknown: 0, ...totals })}`}
    />
  );
  const chipOf = (item: ScopeChipItem) => {
    const meta = metaOf(item.key);
    return (
      <Chip
        key={item.key}
        selected={scope === item.key}
        onClick={() => onScopeChange(item.key)}
        icon={meta.icon}
        label={meta.label}
        count={item.total}
        issues={item.issues}
        title={meta.title}
      />
    );
  };

  return (
    <div className="w-full sm:w-auto sm:min-w-0 sm:flex-1">
      {/* On a phone, seven pills wrapped into four ragged rows; a two-column grid
          lines them up (CTX-1339). */}
      <div className="grid w-full grid-cols-2 gap-2 sm:hidden" role="group" aria-label="Filter by service type">
        {allChip("col-span-2")}
        {items.map((item) => chipOf(item))}
      </div>
      <div className="hidden min-w-0 items-center justify-end gap-2 sm:flex" role="group" aria-label="Filter by service type">
        {allChip()}
        <ChipLane selectedKey={scope} contentKey={onBar.map((item) => `${item.key}:${item.total}:${item.issues.count}`).join("|")}>
          {onBar.map((item) => chipOf(item))}
        </ChipLane>
        {isChoosable && (
          <AllKindsMenu
            items={items}
            offBar={more}
            pinned={pinned}
            metaOf={metaOf}
            scope={scope}
            onScopeChange={onScopeChange}
            onTogglePin={togglePin}
          />
        )}
      </div>
    </div>
  );
}
